import { create } from 'zustand';

import { getApiErrorInfo } from '@common/utils/fetcher';

import { disableFeatureOnError } from '@entities/account';

import {
  getMatchmakerCandidates,
  MATCHMAKER_ERROR_CODES,
} from '@services/MatchmakerService';
import { type User } from '@services/UsersService';

import {
  deckCriteriaKey,
  type MatchmakerDeckCriteria,
} from '../lib/friendCriteria';
import { reportSwipeLimitPaywall } from '../lib/reportSwipeLimitPaywall';
import { writeSwipeRemaining } from '../lib/writeSwipeRemaining';

/** A next page is fetched only when fewer cards than this are left. */
export const MATCHMAKER_DECK_LOW_WATER = 3;

export type MatchmakerDeckRefusal =
  | { kind: 'swipe_limit'; resetTime: string | null }
  | { kind: 'browse_limit'; resetTime: string | null }
  | { kind: 'not_approved' }
  | { kind: 'not_available' }
  | { kind: 'not_matchmaker' }
  | { kind: 'feature_disabled' }
  | { kind: 'city' }
  | { kind: 'failed' };

type MatchmakerDeckState = {
  criteriaKey: string | null;
  cards: User[];
  cursor: string | null;
  isExhausted: boolean;
  isLoading: boolean;
  refusal: MatchmakerDeckRefusal | null;
  handledIds: ReadonlySet<string>;
};

export type MatchmakerDeckStore = MatchmakerDeckState & {
  loadMore: (criteria: MatchmakerDeckCriteria) => Promise<void>;
  removeCard: (cardId: string) => void;
  putBack: (card: User) => void;
  refuse: (refusal: MatchmakerDeckRefusal) => void;
  clearRefusal: () => void;
  reset: () => void;
};

const emptyDeck = (criteriaKey: string | null): MatchmakerDeckState => ({
  criteriaKey,
  cards: [],
  cursor: null,
  isExhausted: false,
  isLoading: false,
  refusal: null,
  handledIds: new Set<string>(),
});

const readResetTime = (details: Record<string, unknown> | null) =>
  typeof details?.reset_time === 'string' ? details.reset_time : null;

/**
 * The deck state a refused matchmaker request leads to, from its
 * `errors.code`; null for anything else (a network error, a 5xx, a
 * request rate limit).
 */
export const deckRefusalOf = (error: unknown): MatchmakerDeckRefusal | null => {
  const { status, code, details } = getApiErrorInfo(error);

  switch (code) {
    case MATCHMAKER_ERROR_CODES.swipeLimit:
      return { kind: 'swipe_limit', resetTime: readResetTime(details) };
    case MATCHMAKER_ERROR_CODES.browseLimit:
      return { kind: 'browse_limit', resetTime: readResetTime(details) };
    case MATCHMAKER_ERROR_CODES.notApproved:
      return { kind: 'not_approved' };
    case MATCHMAKER_ERROR_CODES.notAvailable:
      return { kind: 'not_available' };
    case MATCHMAKER_ERROR_CODES.notMatchmaker:
      return { kind: 'not_matchmaker' };
    case MATCHMAKER_ERROR_CODES.featureDisabled:
      return status === 404 ? { kind: 'feature_disabled' } : null;
  }

  if (status === 400 && details && 'city' in details) return { kind: 'city' };

  return null;
};

/**
 * Turns a refused matchmaker request into a deck state: a mid-session flag
 * flip also turns the feature off in the account (plan §1).
 */
export const refusalFromError = (error: unknown) => {
  disableFeatureOnError(error, 'matchmaker');

  const refusal = deckRefusalOf(error);

  if (refusal?.kind === 'swipe_limit') {
    reportSwipeLimitPaywall();
    // The shared counter is used up: the dating deck reads it too.
    writeSwipeRemaining(0);
  }

  return refusal;
};

const hasPassed = (time: string | null, now: number) =>
  time !== null && Date.parse(time) <= now;

/**
 * Whether a refusal kept from an earlier visit no longer holds, so a new
 * visit asks again: the account-related ones (the deck shows only while the
 * account says matchmaker mode is on; moderation and holds change during a
 * session, and the server checks them before it reserves any card), a
 * refused city (picked again), and the limits once they reset or swipes are
 * back.
 */
export const isDeckRefusalStale = (
  refusal: MatchmakerDeckRefusal,
  swipesRemaining: number | null | undefined,
  now = Date.now(),
) => {
  switch (refusal.kind) {
    case 'not_matchmaker':
    case 'feature_disabled':
    case 'not_approved':
    case 'not_available':
    case 'city':
      return true;
    case 'swipe_limit':
      return swipesRemaining !== 0 || hasPassed(refusal.resetTime, now);
    case 'browse_limit':
      return hasPassed(refusal.resetTime, now);
    default:
      return false;
  }
};

/**
 * The matchmaker's deck (plan §4, review B6). Every candidates request
 * reserves its cards against the day's browse budget, so the deck lives in a
 * module store: a remount makes no request, the next page is fetched with the
 * cursor only when fewer than MATCHMAKER_DECK_LOW_WATER cards are left, and
 * the deck starts over only when the friend's criteria change.
 */
export const useMatchmakerDeckStore = create<MatchmakerDeckStore>()(
  (set, get) => ({
    ...emptyDeck(null),

    loadMore: async (criteria) => {
      const key = deckCriteriaKey(criteria);

      if (get().criteriaKey !== key) set(emptyDeck(key));

      const { cards, cursor, isExhausted, isLoading, refusal } = get();

      if (
        isLoading ||
        isExhausted ||
        refusal ||
        cards.length >= MATCHMAKER_DECK_LOW_WATER
      ) {
        return;
      }

      set({ isLoading: true });

      try {
        const page = await getMatchmakerCandidates({
          ...criteria,
          ...(cursor ? { cursor } : {}),
        });

        if (get().criteriaKey !== key) return;

        set((state) => {
          const knownIds = new Set([
            ...state.handledIds,
            ...state.cards.map((card) => card.id),
          ]);

          return {
            cards: [
              ...state.cards,
              ...page.results.filter((card) => !knownIds.has(card.id)),
            ],
            cursor: page.next_cursor,
            isExhausted: page.next_cursor === null,
            isLoading: false,
          };
        });
      } catch (error) {
        if (get().criteriaKey !== key) return;

        const { code } = getApiErrorInfo(error);

        if (code === MATCHMAKER_ERROR_CODES.invalidCursor && cursor) {
          set({ cursor: null, isLoading: false });

          return get().loadMore(criteria);
        }

        set({
          isLoading: false,
          refusal: refusalFromError(error) ?? { kind: 'failed' },
        });
      }
    },

    removeCard: (cardId) =>
      set((state) => ({
        cards: state.cards.filter((card) => card.id !== cardId),
        handledIds: new Set([...state.handledIds, cardId]),
      })),

    putBack: (card) =>
      set((state) => ({
        cards: [card, ...state.cards.filter((each) => each.id !== card.id)],
        handledIds: new Set(
          [...state.handledIds].filter((cardId) => cardId !== card.id),
        ),
      })),

    refuse: (refusal) => set({ refusal }),

    clearRefusal: () => set({ refusal: null }),

    reset: () => set(emptyDeck(null)),
  }),
);
